import React, { useEffect } from 'react'
import AboutFirst from '../Components/About/AboutFirst'
import Mission from '../Components/About/Mission'
import FeaturedGames from '../Components/About/FeaturesGame'
import AboutHeroSection from '../Components/About/AboutHerosection'
import { useSEO } from './Seo'
import { Helmet } from 'react-helmet-async' 
import { Gamepad2, Globe, Shield, Zap } from 'lucide-react'

const About = () => {

useSEO({
    title: "About Game Zone Hub | Play Free Online Games",
    description:
      "Learn about Game Zone Hub, your ultimate destination to play free online games. Discover our mission to provide the best HTML5 browser games with no downloads required.",
    keywords:
      "about game zone hub, play free online games, online games, play free online, free browser games, html5 games platform, no download games",
    canonical: "https://www.gamezonehub.in/about",
    ogTitle: "About Game Zone Hub - Play Free Online Games",
    ogDescription: "Discover our mission to provide the best free online games instantly. No downloads, just play!",
  });


const STORAGE_KEY = "gameListPage"

useEffect(()=>{
  localStorage.setItem(STORAGE_KEY, String(1))
  window.scrollTo({top:"0"})
},[])

const schemaData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Game Zone Hub",
    "url": "https://www.gamezonehub.in/",
    "logo": "https://www.gamezonehub.in/logo.png",
    "description": "Game Zone Hub is a digital entertainment platform created by gamers to provide high-quality, free online browser games.",
    "foundingDate": "2024", 
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer support",
      "email": "support@gamezonehub.in" 
    },
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 via-black to-gray-800 min-h-screen">
      <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>

      <AboutFirst/>
      <AboutHeroSection/>
      <Mission/>
      <FeaturedGames/>

      {/* 🚀 NATURAL HUMAN WRITTEN ABOUT PAGE CONTENT */}
      <section className="bg-[#0b1120] border-t border-white/10 sm:pt-20 pt-10 sm:pb-20 pb-10 px-4" data-aos="fade-up">
        <div className="container px-4 mx-auto space-y-16">
          
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-extrabold text-white mb-6">The Story Behind Game Zone Hub</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto rounded-full mb-8"></div>
            <p className="text-gray-400 max-w-4xl mx-auto text-lg leading-relaxed">
              We are just a group of friends who love playing games. But we got tired of waiting for massive updates, running out of phone storage, and paying for hidden microtransactions. So, we decided to build something better.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            
            {/* Left Column */}
            <div className="space-y-8 text-gray-300 leading-relaxed text-sm sm:text-base">
              
              <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-3">
                      <Gamepad2 className="text-cyan-400 w-8 h-8" /> Who We Are
                  </h3>
                  <p className="mb-4">
                    Game Zone Hub started as a simple idea in 2024: what if we could gather all the best <strong>free online games</strong> in one place, where anyone could play them instantly? No sign-ups, no downloading sketchy files, just pure fun. 
                  </p>
                  <p>
                    Today, we are a dedicated team that spends hours every week testing and curating new HTML5 browser games. From action-packed multiplayer battles to quiet puzzle games for your morning commute, we want to make sure you always have something fun to play.
                  </p>
              </div>

              <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-3">
                      <Globe className="text-blue-400 w-8 h-8" /> What We Offer
                  </h3>
                  <p>
                    We host hundreds of titles across all major genres. You will find racing games to test your reflexes, brain teasers to keep your mind sharp, and casual games to help you relax. Because we partner directly with talented game developers, our library is always growing with new, high-quality content that you can trust.
                  </p>
              </div>

            </div>

            {/* Right Column */}
            <div className="space-y-8 text-gray-300 leading-relaxed text-sm sm:text-base">
              
              <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-3">
                      <Zap className="text-yellow-400 w-8 h-8" /> Play Anywhere
                  </h3>
                  <p className="mb-4">
                    We believe gaming should fit your lifestyle. Because all our games run directly in your web browser using HTML5, you don't need a powerful gaming PC to enjoy them. 
                  </p>
                  <p>
                    You can start a racing game on your laptop at home, and then switch to playing a matching puzzle on your smartphone while on the bus. Our website automatically adjusts the game to fit your screen size perfectly, ensuring smooth performance whether you are clicking a mouse or tapping a screen.
                  </p>
              </div>

              <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-3">
                      <Shield className="text-green-400 w-8 h-8" /> Safe and Free
                  </h3>
                  <p>
                    Your safety online is important to us. By playing games directly in your browser, you completely avoid the risk of accidentally downloading malware or viruses to your computer. We keep our site 100% free to play by using light, non-intrusive advertising, so you will never hit a surprise paywall. It is a secure, family-friendly space built just for having fun.
                  </p>
              </div>

            </div>

          </div>

          <div className="text-center bg-gradient-to-r from-blue-900/30 to-purple-900/30 p-10 rounded-3xl border border-white/10 mt-16 shadow-2xl">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">Join Our Community</h3>
            <p className="text-gray-400 mb-0 max-w-3xl mx-auto text-sm sm:text-base leading-relaxed">
              We are constantly working to make Game Zone Hub the best place to play on the internet. We listen to our players and are always looking for ways to improve. Thank you for visiting, and we hope you find your next favorite game here!
            </p>
          </div>

        </div>
      </section>

    </div>
  )
}

export default About